import { useState, useEffect, useCallback, useMemo } from 'react';
import { getInsuranceCompanies } from '../api/insurance';
import type { InsuranceCompanyDto } from '../types/insurance';

interface UseInsuranceCompaniesOptions {
  includeInactive?: boolean;
}

export function useInsuranceCompanies(options?: UseInsuranceCompaniesOptions) {
  const [data, setData] = useState<InsuranceCompanyDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const includeInactive = options?.includeInactive ?? false;

  const optionsKey = useMemo(
    () => JSON.stringify({ includeInactive }),
    [includeInactive]
  );

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const isActive = includeInactive ? undefined : true;
      const companies = await getInsuranceCompanies(isActive);
      setData(companies);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch insurance companies');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchCompanies();
  }, [optionsKey, fetchCompanies]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCompanies,
  };
}
